import * as React from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import Papa from "papaparse";
import { v4 as uuid } from "uuid";
import { formatDate } from "date-fns";
import { trpc } from "src/trpc";
import { useBackButton } from "src/hooks/useBackButton";

interface UploadTransactionType {
  id: string;
  type?: string | null;
  accountNumber?: string | null;
  sortCode?: string | null;
  date: string;
  amount: number;
  balance: number;
  description: string;
}

interface Props {
  onClose(): void;
}

interface Row {
  "Transaction Date": string;
  "Transaction Type": string;
  "Sort Code": string;
  "Account Number": string;
  "Transaction Description": string;
  "Debit Amount": string;
  "Credit Amount": string;
  Balance: string;
}

export function UploadTransactions({ onClose }: Props) {
  useBackButton(onClose);
  const utils = trpc.useUtils();
  const { mutateAsync } = trpc.uploadTransactions.useMutation();
  const [file, setFile] = React.useState<File | null>(null);
  const [transactions, setTransactions] = React.useState<UploadTransactionType[]>([]);

  React.useEffect(() => {
    if (!file) {
      return;
    }

    file.text().then((text) => {
      const results = Papa.parse<Row>(text, {
        header: true,
        skipEmptyLines: true,
      });

      if (results.errors.length === 0) {
        setTransactions(
          results.data.map((row) => ({
            id: uuid(),
            date: row["Transaction Date"].split("/").reverse().join("-"),
            type: row["Transaction Type"],
            sortCode: row["Sort Code"],
            accountNumber: row["Account Number"],
            description: row["Transaction Description"],
            amount: row["Debit Amount"]
              ? -parseFloat(row["Debit Amount"])
              : parseFloat(row["Credit Amount"]),
            balance: parseFloat(row["Balance"]),
          })),
        );
      }
    });
  }, [file]);

  const handleSubmit = async () => {
    await mutateAsync({ transactions });
    await utils.getTransactions.invalidate();
    onClose();
  };

  return (
    <Modal show onHide={onClose}>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>File</Form.Label>
          <Form.Control
            type="file"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFile(e.target?.files?.[0] ?? null)
            }
          />
        </Form.Group>
        {transactions.length > 0 && (
          <Table size="sm" striped bordered>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {transactions.slice(0, 2).map((t) => (
                <tr>
                  <td>{formatDate(t.date, "yyyy-MM-dd")}</td>
                  <td>{t.description}</td>
                  <td>{t.amount}</td>
                  <td>{t.balance}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleSubmit} disabled={false}>
          Upload
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
